'use client';
import React, { useEffect, useState } from 'react';
import { SkeletonQuoteCell } from '../SkeletonBoxes';
import { QuoteCell, QuoteCellProps } from '@benzinga/quotes-ui';
import styled from '@benzinga/themetron';
import { DetailedQuote, QuotesManager } from '@benzinga/quotes-manager';
import { SessionContext } from '@benzinga/session-context';

export interface MoversProps {
  symbols: string[] | null;
  cellDataAction?: string;
}

export interface QuoteTransformedCell extends DetailedQuote, QuoteCellProps {}

export const Movers: React.FC<MoversProps> = ({ cellDataAction, symbols = [] }) => {
  const [quotes, setQuotes] = useState<QuoteCellProps[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const session = React.useContext(SessionContext);
  const quoteManager = session.getManager(QuotesManager);

  const loadData = React.useCallback(() => {
    (async () => {
      setIsLoading(true);
      const tickers = symbols ? symbols : [];
      quoteManager.getDelayedQuotes(tickers).then(res => {
        const items: QuoteCellProps[] = tickers
          .map((symbol: string) => {
            const delayedQuote = res?.ok?.[symbol];
            const quote = delayedQuote as Partial<QuoteTransformedCell>;
            quote.price = delayedQuote?.lastTradePrice ? delayedQuote.lastTradePrice : 0;
            quote.absoluteChange = delayedQuote?.change ? delayedQuote.change : 0;
            return quote;
          })
          .filter(i => i.symbol) as QuoteTransformedCell[];
        items.sort((a, b) => {
          const diff = Math.abs(a.changePercent) - Math.abs(b.changePercent);
          if (diff < 0) {
            return -1;
          } else if (diff > 0) {
            return 1;
          } else {
            return 0;
          }
        });
        setQuotes(items);
        setIsLoading(false);
      });
    })();
  }, [quoteManager, symbols]);

  useEffect(() => {
    if (symbols) {
      loadData();
    } else {
      setIsLoading(true);
    }
  }, [loadData, symbols]);

  return (
    <MoversWrapper className="movers">
      <div className="movers-container">
        <MoversBoxWrapper className="movers-box-wrapper">
          {!isLoading && quotes && quotes.length ? (
            <>
              {quotes.map((quote, i) => (
                <QuoteCell {...quote} anchorDataAction={cellDataAction} key={i} />
              ))}
            </>
          ) : (
            <MoversSkeleton />
          )}
        </MoversBoxWrapper>
      </div>
    </MoversWrapper>
  );
};

export const MoversWrapper = styled.div`
  &.movers {
    display: flex;
    flex-direction: column;
    .movers-container {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
  }
`;

export const MoversBoxWrapper = styled.div`
  &.movers-box-wrapper {
    height: 300px;
    overflow-y: scroll;
    background-color: ${({ theme }) => theme.colorPalette.neutral50};
    border: solid 1px ${({ theme }) => theme.colors.border};
    border-radius: 4px;
    .empty-state {
      height: 300px;
    }
    .quote-cell-wrapper {
      display: flex;
      border-bottom-width: 1px;
      padding: 4px 8px;
    }
  }
`;

export const MoversBlank = () => {
  return (
    <MoversBoxWrapper>
      <MoversSkeleton />
    </MoversBoxWrapper>
  );
};

export const MoversSkeleton = () => {
  return (
    <div>
      <SkeletonQuoteCell />
      <SkeletonQuoteCell />
      <SkeletonQuoteCell />
      <SkeletonQuoteCell />
      <SkeletonQuoteCell />
      <SkeletonQuoteCell />
      <SkeletonQuoteCell />
      <SkeletonQuoteCell />
    </div>
  );
};
